import React, { useState } from "react";
import { Car, Tag, Boxes, ThumbsUp, MapPin, Bell, X, ChevronLeft, ChevronRight } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { SocialLinks } from "@/components/store/SocialLinks";

const PERKS = [
  { icon: Car, label: "Drive-through shopping", color: "bg-brand-red" },
  { icon: Tag, label: "Great deals", color: "bg-brand-yellow text-brand-navy" },
  { icon: Boxes, label: "Wide selection", color: "bg-brand-navy" },
  { icon: ThumbsUp, label: "Everything you love", color: "bg-brand-green" },
];

export default function OpeningSoon({ settings }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const s = settings;
  const gallery = s.opening_soon_gallery || [];

  const notify = async (e) => {
    e.preventDefault();
    try { await api.post("/newsletter", { name, email }); setDone(true); toast.success("You're on the list!"); }
    catch { toast.error("Could not sign you up. Try again."); }
  };

  return (
    <main className="bg-white" data-testid="opening-soon-page">
      <section className="relative overflow-hidden bg-brand-navy">
        <img src={mediaUrl(s.opening_soon_image)} alt="Barrhaven Dealswagon Outlet drive-through pickup — opening soon" className="w-full max-h-[78vh] object-contain object-center" data-testid="opening-soon-hero-image" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-white to-transparent" />
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10 relative pb-16">
        <div className="grid lg:grid-cols-[1.2fr_1fr] gap-10 items-start">
          <div>
            <div className="inline-flex items-center gap-2 bg-brand-red text-white font-heading text-lg sm:text-xl px-5 py-2 rounded-full rotate-[-2deg] shadow-lg" data-testid="opening-soon-badge">
              {s.opening_soon_headline}
            </div>
            <h1 className="font-heading text-brand-navy text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[1.02]">
              Drive-through deals<br />are coming to Barrhaven.
            </h1>
            <p className="text-slate-700 text-base md:text-lg mt-5 max-w-xl" data-testid="opening-soon-message">{s.opening_soon_message}</p>
            {s.opening_soon_date_text && <div className="mt-4 inline-block bg-brand-yellowLight text-brand-navy font-black uppercase tracking-widest text-sm px-4 py-2 rounded-full" data-testid="opening-soon-date">{s.opening_soon_date_text}</div>}

            <div className="mt-8 flex items-start gap-3 bg-brand-greenLight border border-brand-green/30 rounded-2xl p-5" data-testid="opening-soon-location">
              <div className="bg-brand-green text-white rounded-xl p-2.5 shrink-0"><MapPin className="h-5 w-5" /></div>
              <div>
                <div className="text-xs font-black uppercase tracking-widest text-brand-greenDark">{s.opening_soon_location_label}</div>
                <div className="font-heading text-brand-navy text-lg mt-1">{s.opening_soon_location}</div>
              </div>
            </div>

            <div className="mt-8 grid sm:grid-cols-2 gap-3">
              {PERKS.map(({ icon: Icon, label, color }) => (
                <div key={label} className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl p-3">
                  <div className={`${color} text-white rounded-full p-2.5`}><Icon className="h-5 w-5" /></div>
                  <div className="font-heading text-brand-navy uppercase text-sm">{label}</div>
                </div>
              ))}
            </div>

            <SocialLinks settings={s} iconClass="h-6 w-6" className="mt-8 text-brand-navy" />
          </div>

          <aside className="bg-brand-navy text-white rounded-3xl p-7 lg:sticky lg:top-24 shadow-2xl" data-testid="opening-soon-notify">
            <div className="flex items-center gap-2 text-brand-yellow font-black uppercase tracking-widest text-xs"><Bell className="h-4 w-4" /> Get notified</div>
            <div className="font-heading text-2xl mt-2">Be first at the drive-through</div>
            <p className="text-slate-300 text-sm mt-2">Opening-day alerts, first-look deals and inaugural specials — straight to your inbox.</p>
            {done ? (
              <div className="mt-5 bg-brand-green/20 border border-brand-green rounded-xl p-4 font-bold" data-testid="notify-success">You're on the list. See you at the drive-through!</div>
            ) : (
              <form onSubmit={notify} className="mt-5 space-y-3">
                <input data-testid="notify-name" placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} className="w-full h-12 rounded-xl px-4 text-slate-900" />
                <input data-testid="notify-email" type="email" required placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full h-12 rounded-xl px-4 text-slate-900" />
                <button type="submit" data-testid="notify-submit" className="w-full h-12 rounded-full bg-brand-red hover:bg-brand-redHover font-black uppercase tracking-wider transition-colors">Notify me</button>
              </form>
            )}
          </aside>
        </div>

        {gallery.length > 0 && (
          <div className="mt-16" data-testid="opening-soon-gallery">
            <div className="text-center">
              <div className="inline-block bg-brand-yellowLight text-brand-navy font-black uppercase tracking-widest text-xs px-4 py-2 rounded-full">A sneak peek</div>
              <h2 className="font-heading text-brand-navy text-3xl sm:text-4xl mt-3">Take a look around</h2>
            </div>
            <div className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-4">
              {gallery.map((img, idx) => (
                <button key={img.id} onClick={() => setLightbox(idx)} data-testid={`gallery-photo-${img.id}`}
                  className="text-left rounded-2xl overflow-hidden border border-slate-200 bg-white cursor-zoom-in group">
                  <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                    <img src={mediaUrl(img.url)} alt={img.caption || "Barrhaven Dealswagon Outlet"} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  {img.caption && <div className="px-3 py-2.5 text-sm text-slate-600 text-center font-medium" data-testid={`gallery-caption-text-${img.id}`}>{img.caption}</div>}
                </button>
              ))}
            </div>
          </div>
        )}

        {lightbox !== null && gallery[lightbox] && (
          <div className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4" data-testid="lightbox" onClick={() => setLightbox(null)}>
            <button onClick={() => setLightbox(null)} data-testid="lightbox-close" className="absolute top-4 right-4 text-white/80 hover:text-white"><X className="h-8 w-8" /></button>
            {gallery.length > 1 && (
              <button onClick={(e) => { e.stopPropagation(); setLightbox((i) => (i - 1 + gallery.length) % gallery.length); }} data-testid="lightbox-prev"
                className="absolute left-2 sm:left-6 text-white/80 hover:text-white"><ChevronLeft className="h-10 w-10" /></button>
            )}
            <div className="max-w-4xl" onClick={(e) => e.stopPropagation()}>
              <img src={mediaUrl(gallery[lightbox].url)} alt={gallery[lightbox].caption || ""} className="max-h-[80vh] w-auto mx-auto rounded-lg" data-testid="lightbox-image" />
              {gallery[lightbox].caption && <div className="text-white text-center mt-3 text-sm" data-testid="lightbox-caption">{gallery[lightbox].caption}</div>}
            </div>
            {gallery.length > 1 && (
              <button onClick={(e) => { e.stopPropagation(); setLightbox((i) => (i + 1) % gallery.length); }} data-testid="lightbox-next"
                className="absolute right-2 sm:right-6 text-white/80 hover:text-white"><ChevronRight className="h-10 w-10" /></button>
            )}
          </div>
        )}
      </section>
    </main>
  );
}
